import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Photo } from '@/components/ui/Photo';
import type { PhotoName } from '@/features/site/media';
import { cn } from '@/lib/utils';

export interface Crumb {
  label: string;
  to?: string;
}

interface PageHeroProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  intro?: ReactNode;
  crumbs?: Crumb[];
  actions?: ReactNode;
  photo?: PhotoName;
  photoPosition?: string;
  /** Extra content under the intro. */
  children?: ReactNode;
}

function Breadcrumb({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="text-[13px] text-muted">
      <ol className="flex flex-wrap items-center gap-1.5">
        <li>
          <Link to="/" className="hover:text-primary">
            Home
          </Link>
        </li>
        {crumbs.map((c, i) => (
          <li key={c.label} className="flex items-center gap-1.5">
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            {c.to && i < crumbs.length - 1 ? (
              <Link to={c.to} className="hover:text-primary">
                {c.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-ink-soft">
                {c.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Shared inner-page header: breadcrumb (full width, top), eyebrow, editorial h1, intro, optional photograph. */
export function PageHero({ eyebrow, title, intro, crumbs, actions, photo, photoPosition, children }: PageHeroProps) {
  return (
    <section className="bg-surface">
      {crumbs && crumbs.length > 0 && (
        <div className="container-page pt-10 lg:pt-12">
          <Breadcrumb crumbs={crumbs} />
        </div>
      )}
      <div className={cn('container-page grid gap-12 pt-8 pb-14 lg:pt-10 lg:pb-20', photo && 'lg:grid-cols-12 lg:items-center')}>
        <div className={cn(photo ? 'lg:col-span-7' : 'max-w-[820px]')}>
          {eyebrow && (
            <p className="eyebrow flex items-center gap-3 text-accent-dark">
              <span aria-hidden className="h-px w-8 bg-accent" />
              {eyebrow}
            </p>
          )}
          <h1 className="display-lg mt-4 text-primary">{title}</h1>
          {intro && <div className="lede mt-6 max-w-[620px]">{intro}</div>}
          {actions && <div className="mt-9 flex flex-col gap-3 sm:flex-row">{actions}</div>}
          {children}
        </div>
        {photo && (
          <figure className="lg:col-span-5">
            <div className="aspect-[4/3] overflow-hidden rounded-[8px] bg-primary/10 lg:aspect-[5/6]">
              <Photo name={photo} priority sizes="(min-width: 1024px) 40vw, 100vw" position={photoPosition} />
            </div>
            <figcaption className="mt-2 text-[12px] text-muted">Illustrative industry photograph</figcaption>
          </figure>
        )}
      </div>
    </section>
  );
}
